import React, { createContext, useContext, useEffect, useState } from "react";
import { auth } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "./firebase";
import { migrateGuestAttempts, hasGuestAttempts, migrateLocalStorageGuestAttempts, hasLocalStorageGuestAttempts } from "./utils";

// =============================================
// AuthProvider.js - Provides authentication context for the app.
// 
// This file handles user authentication state and automatically saves SSO user data
// (Google, Microsoft) to Firestore users collection when they log in for the first time.
// It also creates guest profiles for anonymous users and manages user permissions.
// NEW: Includes guest to registered user migration functionality.
//
// IMPORTANT: Never store or update the password in Firestore for SSO users.
// Passwords are managed securely by Firebase Auth and should not be stored in Firestore.
//
// Features:
// - Automatic SSO user data saving to Firestore (without password)
// - Guest user creation for anonymous users
// - User permission management
// - Profile data synchronization
// - Guest to registered user migration
//
// Last updated: 2025-07-04 00:45:00 UTC
// =============================================

// ---
// GUEST PROFILE CREATION:
// When a user (including anonymous/guest) signs in, ensure a user profile exists in Firestore.
// If not, create a profile with role: 'guest' and userType: 'guest'.
// This ensures all users (including guests) can save attempts and are recognized by Firestore rules.
// ---

// ---
// GUEST TO REGISTERED USER MIGRATION:
// When a guest user registers or logs in, their previous exam attempts are automatically
// transferred to their new registered account. This preserves their exam history and
// provides a seamless user experience.
// ---

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userType, setUserType] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isPremiumUser, setIsPremiumUser] = useState(false);
  const [isPaidUser, setIsPaidUser] = useState(false);
  const [isRegisteredUser, setIsRegisteredUser] = useState(false);
  const [canAccessCourse, setCanAccessCourse] = useState(false);
  const [canAccessDashboard, setCanAccessDashboard] = useState(false);
  const [canManageQuestions, setCanManageQuestions] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        const tokenResult = await firebaseUser.getIdTokenResult();
        const claimRole = tokenResult.claims.role;
        const isAdminClaim = claimRole === "admin";
        const isQuestionEditorClaim = claimRole === "question_editor";
        setIsAdmin(isAdminClaim);
        setCanManageQuestions(isAdminClaim || isQuestionEditorClaim);
        
        // Fetch or create user profile in Firestore
        try {
          const userDocRef = doc(db, "users", firebaseUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          console.log('[AuthProvider] Firebase user:', firebaseUser.uid, firebaseUser.email);
          console.log('[AuthProvider] User provider data:', firebaseUser.providerData);
          console.log('[AuthProvider] userDocSnap.exists():', userDocSnap.exists());
          
          if (!userDocSnap.exists()) {
            // Create new user profile - handle different authentication methods
            let userData = {
              email: firebaseUser.email || "",
              role: isAdminClaim ? "admin" : (isQuestionEditorClaim ? "question_editor" : "student"),
              userType: isAdminClaim ? "admin" : (isQuestionEditorClaim ? "question_editor" : "registered"),
              createdAt: new Date(),
              updatedAt: new Date()
            };

            // Check if user signed in with SSO (Google or Microsoft)
            if (firebaseUser.providerData && firebaseUser.providerData.length > 0) {
              const providerData = firebaseUser.providerData[0];
              console.log('[AuthProvider] SSO provider data:', providerData);
              
              // Extract name from SSO provider
              if (providerData.displayName) {
                const nameParts = providerData.displayName.split(' ');
                userData.firstName = nameParts[0] || "";
                userData.lastName = nameParts.slice(1).join(' ') || "";
                console.log('[AuthProvider] Extracted name from SSO:', userData.firstName, userData.lastName);
              }
              
              // Add photo URL if available
              if (providerData.photoURL) {
                userData.photoURL = providerData.photoURL;
                console.log('[AuthProvider] SSO photo URL:', userData.photoURL);
              }
              
              // Add provider information
              userData.provider = providerData.providerId;
              userData.providerUid = providerData.uid;
              console.log('[AuthProvider] SSO provider:', userData.provider);
            } else {
              // Email/password user - set default name
              userData.firstName = "User";
              userData.lastName = "";
              console.log('[AuthProvider] Email/password user - using default name');
            }

            // Save new user profile to Firestore (never includes password for SSO)
            await setDoc(userDocRef, userData);
            console.log('[AuthProvider] Created new user profile in Firestore:', userData);
            
            // Set profile data for immediate use
            setUserProfile(userData);
            setUserType(userData.userType);
            setIsPremiumUser(false);
            setIsPaidUser(false);
            setIsRegisteredUser(true);
            setCanAccessCourse(false);
            setCanAccessDashboard(false);
            setCanManageQuestions(isAdminClaim || isQuestionEditorClaim);
            
          } else {
            // User profile exists - load and potentially update
            const profileData = userDocSnap.data();
            console.log('[AuthProvider] Loaded existing profileData:', profileData);
            
            // Check if we need to update SSO data for existing users
            if (firebaseUser.providerData && firebaseUser.providerData.length > 0) {
              const providerData = firebaseUser.providerData[0];
              const needsUpdate = !profileData.provider || 
                                profileData.provider !== providerData.providerId ||
                                profileData.photoURL !== providerData.photoURL ||
                                !profileData.providerUid;
              
              if (needsUpdate) {
                console.log('[AuthProvider] Updating existing user with SSO data');
                const updateData = {
                  updatedAt: new Date(),
                  provider: providerData.providerId,
                  providerUid: providerData.uid
                };
                
                // Update name if not already set or if SSO name is available
                if (providerData.displayName && (!profileData.firstName || profileData.firstName === "User")) {
                  const nameParts = providerData.displayName.split(' ');
                  updateData.firstName = nameParts[0] || "";
                  updateData.lastName = nameParts.slice(1).join(' ') || "";
                  console.log('[AuthProvider] Updated name from SSO:', updateData.firstName, updateData.lastName);
                }
                
                // Update photo URL if available
                if (providerData.photoURL) {
                  updateData.photoURL = providerData.photoURL;
                  console.log('[AuthProvider] Updated photo URL from SSO:', updateData.photoURL);
                }
                
                // Update the document
                await updateDoc(userDocRef, updateData);
                console.log('[AuthProvider] Updated user profile with SSO data:', updateData);
                
                // Update local profile data
                const updatedProfile = { ...profileData, ...updateData };
                setUserProfile(updatedProfile);
              } else {
                setUserProfile(profileData);
              }
            } else {
              setUserProfile(profileData);
            }
            
            // Set user permissions and type
            setUserType(profileData.userType || "registered");
            setIsPremiumUser(profileData.isPremiumUser || false);
            setIsPaidUser(profileData.isPaidUser || false);
            setIsRegisteredUser(profileData.isRegisteredUser || false);
            setCanAccessCourse(profileData.canAccessCourse || false);
            setCanAccessDashboard(profileData.canAccessDashboard || false);
            const profileCanManageQuestions =
              profileData?.role === "question_editor" ||
              profileData?.userType === "question_editor";
            setCanManageQuestions(tokenResult.claims.role === "admin" || profileCanManageQuestions);
          }
        } catch (error) {
          console.error("[AuthProvider] Error handling user profile:", error);
          setUserType(isAdminClaim ? "admin" : (isQuestionEditorClaim ? "question_editor" : "registered"));
          setIsPremiumUser(false);
          setIsPaidUser(false);
          setIsRegisteredUser(true);
          setCanAccessCourse(false);
          setCanAccessDashboard(false);
          setCanManageQuestions(isAdminClaim || isQuestionEditorClaim);
        }
      } else {
        // No authenticated user
        setUser(null);
        setIsAdmin(false);
        setUserType(null);
        setUserProfile(null);
        setIsPremiumUser(false);
        setIsPaidUser(false);
        setIsRegisteredUser(false);
        setCanAccessCourse(false);
        setCanAccessDashboard(false);
        setCanManageQuestions(false);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    // =============================================
    // Guest User Creation Fix (2025-07-04 UTC)
    // Only create a guest user profile in Firestore if the user is authenticated (including anonymous).
    // For unauthenticated guests, do not access Firestore, just allow browsing public data.
    // This prevents permission errors for true guests on the landing page.
    // =============================================
    if (!user && loading === false) {
      // Do NOT create or read a guest user profile in Firestore for unauthenticated guests.
      // Just allow them to browse public data as a guest.
      return;
    }
    if (user && loading === false && !localStorage.getItem('guestUserRegistered')) {
      // Only create a guest user profile if user is authenticated (anonymous sign-in)
      console.log('[AuthProvider] Anonymous user detected. Creating guest user profile in Firestore.');
      const guestEmail = "";
      const guestId = user.uid;
      const guestDocRef = doc(db, "users", guestId);
      getDoc(guestDocRef).then((docSnap) => {
        if (!docSnap.exists()) {
          setDoc(guestDocRef, {
            email: guestEmail,
            firstName: "Guest",
            lastName: "",
            role: "guest",
            userType: "guest"
          }).then(() => {
            localStorage.setItem('guestUserRegistered', 'true');
            localStorage.setItem('guestUserId', guestId);
          });
        } else {
          localStorage.setItem('guestUserRegistered', 'true');
          localStorage.setItem('guestUserId', guestId);
        }
      });
    } else {
      if (user) {
        console.log('[AuthProvider] Authenticated user detected, NOT creating guest user.');
      } else if (loading) {
        console.log('[AuthProvider] Still loading auth state, NOT creating guest user yet.');
      }
    }
  }, [user, loading]);

  useEffect(() => {
    // =============================================
    // Guest to Registered User Migration
    //
    // When a user registers or logs in, check if they have guest attempts in localStorage
    // and migrate them to their new registered account. This preserves their exam history.
    // =============================================
    const handleGuestMigration = async () => {
      if (!user) return;
      
      const guestId = localStorage.getItem('guestId');
      
      // Check for localStorage guest attempts even without guestId (new in-memory flow)
      const hasLocalStorageAttempts = hasLocalStorageGuestAttempts();
      
      // Skip migration if no guestId AND no localStorage attempts
      if (!guestId && !hasLocalStorageAttempts) return;
      
      console.log('[AuthProvider] Checking for guest attempts to migrate for user:', user.uid);
      
      try {
        let totalMigrated = 0;
        let migrationErrors = [];
        
        // Check for localStorage guest attempts (new method for guests who use in-memory results)
        if (hasLocalStorageAttempts) {
          console.log('[AuthProvider] Found localStorage guest attempts, starting migration...');
          
          const localStorageResult = await migrateLocalStorageGuestAttempts(user.uid);
          
          if (localStorageResult.success) {
            totalMigrated += localStorageResult.migratedCount;
            console.log('[AuthProvider] Successfully migrated', localStorageResult.migratedCount, 'localStorage attempts');
          } else {
            console.error('[AuthProvider] localStorage migration failed:', localStorageResult.error);
            migrationErrors.push(`localStorage: ${localStorageResult.error}`);
          }
        }
        
        // Check for Firebase guest attempts (legacy method for existing guests)
        if (guestId) {
          const hasFirebaseAttempts = await hasGuestAttempts(guestId);
          if (hasFirebaseAttempts) {
            console.log('[AuthProvider] Found Firebase guest attempts, starting migration...');
            
            const firebaseResult = await migrateGuestAttempts(user.uid, guestId);
            
            if (firebaseResult.success) {
              totalMigrated += firebaseResult.migratedCount;
              console.log('[AuthProvider] Successfully migrated', firebaseResult.migratedCount, 'Firebase attempts');
            } else {
              console.error('[AuthProvider] Firebase migration failed:', firebaseResult.error);
              migrationErrors.push(`Firebase: ${firebaseResult.error}`);
            }
          }
        }
        
        // Report final results
        if (totalMigrated > 0) {
          console.log('[AuthProvider] Total migration completed successfully:', totalMigrated, 'attempts');
          // Store migration success for user feedback
          localStorage.setItem('migrationSuccess', JSON.stringify({
            count: totalMigrated,
            timestamp: new Date().toISOString()
          }));
        } else if (!hasLocalStorageAttempts && !guestId) {
          console.log('[AuthProvider] No guest attempts found to migrate');
          // Clear guest session data even if no attempts
          localStorage.removeItem('guestId');
          localStorage.removeItem('isGuest');
          localStorage.removeItem('guestUserRegistered');
          localStorage.removeItem('guestUserId');
        }
        
        // Handle migration errors
        if (migrationErrors.length > 0) {
          console.error('[AuthProvider] Migration completed with errors:', migrationErrors);
          localStorage.setItem('migrationErrors', JSON.stringify({
            errors: migrationErrors,
            timestamp: new Date().toISOString()
          }));
        }
      } catch (error) {
        console.error('[AuthProvider] Error during guest migration:', error);
      }
    };
    
    // Only run migration once when user is first authenticated
    if (user && !user.isAnonymous) {
      handleGuestMigration();
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      isAdmin, 
      userType, 
      userProfile,
      isPremiumUser,
      isPaidUser,
      isRegisteredUser,
      canAccessCourse,
      canAccessDashboard,
      canManageQuestions
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  return useContext(AuthContext);
} 
